/* eslint no-param-reassign: 0, no-unused-vars: 0, no-async-promise-executor: 0 */
import { get } from 'lodash';
import JobUtils from 'client/util/JobUtils';
import SystemInsightsMetadata from 'client/systems/systemInsights/SystemInsightsMetadata';
import Vue from 'vue';
import moment from 'moment';

const { metricGroupMetadata, metricGroups } = SystemInsightsMetadata;

export default {
  namespaced: true,
  state: {
    /**
     * The state is used to store System Insights data by metric group
     * state: {
     *   insightsData: {
     *     systemControls: {
     *       [{ name: 'Controls 1' }, { name: 'Controls 2' }],
     *     },
     *   },
     * }
     */
    insightsData: {},
  },
  actions: {
    /**
     * Exports all System Insights data for a given system ID and metric group
     * @param {String} id - The system ID to retrieve System Insights for
     * @param {String} metricGroup - The System Insights metric group to retrieve data for
     */
    exportInsightsData({ dispatch, state }, { id, metricGroup }) {
      if (!id) throw new Error('Missing system ID');
      if (!metricGroup) throw new Error('Missing metric group');

      return new Promise(async (resolve, reject) => {
        // Get the export data
        const { apiMethod, columns } = metricGroupMetadata[metricGroup];
        let exportInfo;
        if (columns) {
          // TODO: Could the fetchInsightsData action be used here instead of always re-fetching?
          exportInfo = await apiMethod(id, { bulk: true })
            .catch(err => reject(err));
        } else {
          exportInfo = await dispatch('fetchInsightsData', { id, metricGroup })
            .catch(err => reject(err));
        }

        const currentDate = moment().format('YY/MM/DD');
        const fileName = `${metricGroup}-${id}-${currentDate}`;
        JobUtils.downloadCsvTemplate(exportInfo, fileName);
        return resolve();
      });
    },

    /**
     * Fetch System Insights data for a given system ID and metric group
     * @param {String} id - The system ID to retrieve System Insights for
     * @param {String} metricGroup - The System Insights metric group to retrieve data for
     * @param {Object} params - Any parameters to be passed to the API call
     */
    fetchInsightsData({ commit, state }, { id, metricGroup, params = {} } = {}) {
      if (!id) throw new Error('Missing system ID');
      if (!metricGroup) throw new Error('Missing metric group');

      // For tests where state hasn't been initialized
      if (typeof state === 'undefined') return Promise.reject();

      const { apiMethod, table } = metricGroupMetadata[metricGroup];

      const limit = params.limit || 10;
      const skip = params.skip || 0;

      // Check whether the data exists already
      if (state.insightsData[metricGroup]) {
        const recordsNeeded = [];
        for (let i = skip; i < skip + limit; i += 1) {
          recordsNeeded.push(i);
        }
        if (recordsNeeded.every(record => state.insightsData[metricGroup][record])) {
          return new Promise((resolve) => {
            const data = recordsNeeded.map(record => state.insightsData[metricGroup][record]);
            resolve(data);
          });
        }
      }

      if (table === metricGroups.users) {
        return apiMethod(id, { params })
          .then((resp) => {
            const filteredResults = [];
            if (resp.data) {
              resp.data.forEach((item) => {
                if (item.ad_managed && !item.username) {
                  return;
                }
                filteredResults.push(item);
              });
              commit('SET_INSIGHTS_DATA', {
                data: filteredResults,
                metricGroup,
                skip,
                totalCount: filteredResults.length,
              });
            }

            return filteredResults;
          });
      }

      // Fetch the data if it doesn't exist
      return apiMethod(id, { params })
        .then((resp) => {
          if (resp.data) {
            // Store fetched data in state
            commit('SET_INSIGHTS_DATA', {
              data: resp.data,
              metricGroup,
              skip,
              totalCount: parseInt(get(resp, 'headers.x-total-count', 0), 10),
            });
          }

          return resp.data;
        });
    },

    resetState({ commit }) {
      commit('RESET_STATE');
    },
  },
  getters: {
    ableToExportMetricGroupData(state) {
      return (metricGroup) => {
        const { columns } = metricGroupMetadata[metricGroup];
        const metricGroupData = state.insightsData[metricGroup];
        if (columns) {
          return metricGroupData && metricGroupData.totalCount > 0;
        }
        return !!metricGroupData;
      };
    },

    getMetricGroupTotalCount(state) {
      return (metricGroup) => {
        const metricGroupData = state.insightsData[metricGroup];
        return metricGroupData ? metricGroupData.totalCount : 0;
      };
    },
  },
  mutations: {
    RESET_STATE(state) {
      Vue.set(state, 'insightsData', {});
    },

    SET_INSIGHTS_DATA(state, payload) {
      const {
        data,
        metricGroup,
        skip = 0,
        totalCount,
      } = payload;

      if (!state.insightsData[metricGroup]) {
        Vue.set(state.insightsData, metricGroup, {});
      }

      // Store data in object with proper indexing
      data.forEach((datum, i) => {
        Vue.set(state.insightsData[metricGroup], +skip + i, datum);
      });

      // Store total count
      Vue.set(state.insightsData[metricGroup], 'totalCount', totalCount);
    },
  },
};
